import { useMemo, useState, useRef } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Tooltip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useDateSort } from '../../../hooks/useDateSort';
import TableActionsMenu from '../../common/TableActionsMenu';
import type { PointhomeMoneyArcadeRevenueItem } from '../../../types/pointhome.types';
import type { Dayjs } from 'dayjs';

type Props = {
  data: PointhomeMoneyArcadeRevenueItem[];
  startDate?: Dayjs | null;
  endDate?: Dayjs | null;
};

export default function MoneyArcadeRevenueTable({ data, startDate, endDate }: Props) {
  const rowsPerPage = 31;
  const [page, setPage] = useState(0);
  const tableRef = useRef<HTMLTableElement>(null);

  const aggregatedData = useMemo(() => {
    const byDate: Record<string, PointhomeMoneyArcadeRevenueItem> = {};
    data.forEach((row) => {
      if (!byDate[row.date]) {
        byDate[row.date] = { date: row.date, appName: '', revenue: 0, expectedRevenue: 0, req: 0, res: 0, imp: 0, clk: 0 };
      }
      byDate[row.date].revenue += row.revenue;
      byDate[row.date].expectedRevenue += row.expectedRevenue;
      byDate[row.date].req += row.req;
      byDate[row.date].res += row.res;
      byDate[row.date].imp += row.imp;
      byDate[row.date].clk += row.clk;
    });
    return Object.values(byDate);
  }, [data]);

  const { sorted, sortDirection, toggleSort } = useDateSort(aggregatedData, undefined, 'desc');

  const baseSorted = useMemo(() => {
    return [...aggregatedData].sort((a, b) => b.date.localeCompare(a.date));
  }, [aggregatedData]);


  const paged = useMemo(
    () => sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sorted, page]
  );

  const filename = useMemo(() => {
    if (startDate && endDate) return `${startDate.format('YYMMDD')}-${endDate.format('YYMMDD')}`;
    return undefined;
  }, [startDate, endDate]);

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' }).format(value);

  const calculateRevenueDelta = (currentRow: PointhomeMoneyArcadeRevenueItem) => {
    const currentIndex = baseSorted.findIndex((item) => item.date === currentRow.date);
    if (currentIndex === -1 || currentIndex === baseSorted.length - 1) return '-';
    const prevRow = baseSorted[currentIndex + 1];
    if (!prevRow) return '-';

    const delta = currentRow.revenue - prevRow.revenue;
    const rawDeltaPercentage = prevRow.revenue > 0 ? Math.round((delta / prevRow.revenue) * 100) : 0;
    const isPositive = delta >= 0;
    const arrow = isPositive ? '▲' : '▼';
    const sign = isPositive ? '+' : '';

    return (
      <Box
        component="span"
        sx={{ color: isPositive ? '#D32F2F' : '#1976D2', display: 'inline-flex', alignItems: 'center' }}
      >
        {arrow} {rawDeltaPercentage.toLocaleString('ko-KR')}% ({sign}{formatCurrency(delta)})
      </Box>
    );
  };

  const calculateFillRate = (req: number, imp: number) =>
    req > 0 ? ((imp / req) * 100).toFixed(2) : '0.00';

  const calculateCTR = (imp: number, clk: number) =>
    imp > 0 ? ((clk / imp) * 100).toFixed(2) : '0.00';

  const calculateECPM = (revenue: number, imp: number) =>
    imp > 0 ? formatCurrency((revenue / imp) * 1000) : formatCurrency(0);

  const average = (vals: Array<number | undefined>) => {
    const nums = vals.filter((v): v is number => v !== undefined);
    return nums.length ? nums.reduce((s, v) => s + v, 0) / nums.length : 0;
  };

  const totals = useMemo(() => {
    const totalRevenue = sorted.reduce((sum, row) => sum + row.revenue, 0);
    const totalExpectedRevenue = sorted.reduce((sum, row) => sum + row.expectedRevenue, 0);
    const totalReq = sorted.reduce((sum, row) => sum + row.req, 0);
    const totalRes = sorted.reduce((sum, row) => sum + row.res, 0);
    const totalImp = sorted.reduce((sum, row) => sum + row.imp, 0);
    const totalClk = sorted.reduce((sum, row) => sum + row.clk, 0);

    const avgFillRate = average(
      sorted.map((row) => (row.req > 0 ? (row.imp / row.req) * 100 : undefined))
    ).toFixed(2);

    const avgCTR = average(
      sorted.map((row) =>
        row.imp > 0 && row.clk <= row.imp ? (row.clk / row.imp) * 100 : undefined
      )
    ).toFixed(2);

    const avgECPM = formatCurrency(
      average(sorted.map((row) => (row.imp > 0 ? (row.revenue / row.imp) * 1000 : undefined)))
    );

    return { totalRevenue, totalExpectedRevenue, totalReq, totalRes, totalImp, totalClk, avgFillRate, avgCTR, avgECPM };
  }, [sorted]);

  const allRowsDataForExcel = useMemo(
    () =>
      sorted.map((row) => {
        const currentIndex = baseSorted.findIndex((item) => item.date === row.date);
        const prevRow =
          currentIndex !== -1 && currentIndex < baseSorted.length - 1
            ? baseSorted[currentIndex + 1]
            : null;

        let revenueDeltaText = '-';
        if (prevRow) {
          const delta = row.revenue - prevRow.revenue;
          const pct = prevRow.revenue > 0 ? Math.round((delta / prevRow.revenue) * 100) : 0;
          const arrow = delta >= 0 ? '▲' : '▼';
          const sign = delta >= 0 ? '+' : '-';
          revenueDeltaText = `${arrow} ${Math.abs(pct).toLocaleString('ko-KR')}% (${sign}${Math.abs(delta).toLocaleString('ko-KR')})`;
        }

        const fillRate = row.req > 0 ? row.imp / row.req : 0;
        const ctr = row.imp > 0 ? row.clk / row.imp : 0;

        return [
          row.date,
          Math.round(row.revenue).toLocaleString('ko-KR'),
          Math.round(row.expectedRevenue).toLocaleString('ko-KR'),
          revenueDeltaText,
          row.req.toLocaleString(),
          row.res.toLocaleString(),
          fillRate,
          row.imp.toLocaleString(),
          row.clk.toLocaleString(),
          ctr,
          Math.round((row.revenue / Math.max(row.imp, 1)) * 1000).toLocaleString('ko-KR'),
        ];
      }),
    [sorted, baseSorted]
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 1 }}>
        <TableActionsMenu
          tableRef={tableRef}
          allRowsData={allRowsDataForExcel}
          filename={filename}
          sheetName="머니오락실 매출"
        />
      </Box>
      <TableContainer sx={{ overflowX: 'auto', mt: 2 }}>
        <Table ref={tableRef}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                <TableSortLabel active direction={sortDirection} onClick={toggleSort}>
                  날짜
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>총 매출</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>예상 수익</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>매출 증감률</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Request</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Response</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Fill Rate</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Impression</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Click</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>CTR</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>eCPM</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* 합계 행 */}
            <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
              <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>합계</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                {formatCurrency(totals.totalRevenue)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                {formatCurrency(totals.totalExpectedRevenue)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>-</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                {totals.totalReq.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                {totals.totalRes.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  {totals.avgFillRate}%
                  <Tooltip
                    title={
                      <span style={{ whiteSpace: 'pre-line', display: 'block', textAlign: 'center' }}>
                        {'조회한 기간의 평균입니다\n* (노출수 / 요청수)'}
                      </span>
                    }
                    arrow
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: '#666', cursor: 'help' }} />
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                {totals.totalImp.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                {totals.totalClk.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  {totals.avgCTR}%
                  <Tooltip title="조회한 기간의 평균입니다" arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: '#666', cursor: 'help' }} />
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  {totals.avgECPM}
                  <Tooltip title="조회한 기간의 평균입니다" arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: '#666', cursor: 'help' }} />
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
            {/* 데이터 행 */}
            {sorted.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                  데이터가 없습니다.
                </TableCell>
              </TableRow>
            ) : (
              paged.map((row) => (
                <TableRow key={row.date}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.date}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatCurrency(row.revenue)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatCurrency(row.expectedRevenue)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{calculateRevenueDelta(row)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{row.req.toLocaleString()}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{row.res.toLocaleString()}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{calculateFillRate(row.req, row.imp)}%</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{row.imp.toLocaleString()}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{row.clk.toLocaleString()}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{calculateCTR(row.imp, row.clk)}%</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{calculateECPM(row.revenue, row.imp)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={sorted.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[rowsPerPage]}
      />
    </Box>
  );
}
